import { useRef, useState } from 'react';
import { Eye, ImageOff, Link as LinkIcon, Upload, X } from 'lucide-react';
import { useToast } from '@kss/ui';

/**
 * Single-image input that accepts BOTH a hosted URL and an uploaded
 * file. Used by Banner, Employee, Company-logo, User-profile-avatar
 * forms — all the surfaces that need exactly one image.
 *
 *  - **Paste URL** → typed into the URL field, committed via Enter
 *    or the +-button. Validated as `http(s)://…`.
 *  - **Upload** → opens a file picker, reads as base64 data URL,
 *    enforces a per-instance size cap.
 *  - **Preview** → live <img> tile next to the controls. The
 *    "Open" eye button pops the image in a new tab so the admin
 *    can inspect at full resolution.
 *
 * `value` is whatever the parent persists (URL or data URL). The
 * parent form is responsible for deciding what to send to the
 * backend — call `await persistImage(value)` at save time, which
 * uploads any `data:` URL to `/api/Image/upload` and returns the
 * persisted absolute URL. Hosted URLs pass through unchanged.
 */

const DEFAULT_MAX_BYTES = 200 * 1024; // 200 KB — safe for Banner + logos

export interface ImagePickerProps {
  /** Current image — either a hosted URL or a `data:` URL. */
  value: string;
  onChange: (next: string) => void;
  /** Optional label rendered above the controls. */
  label?: string;
  /** Optional hint shown beneath the controls. */
  hint?: string;
  /** Override the upload size cap (default 200 KB). */
  maxBytes?: number;
  /** Square preview tile size in px (default 80). */
  previewSize?: number;
  /** Disable the entire control. */
  disabled?: boolean;
}

export default function ImagePicker({
  value,
  onChange,
  label,
  hint,
  maxBytes = DEFAULT_MAX_BYTES,
  previewSize = 80,
  disabled,
}: ImagePickerProps) {
  const toast = useToast();
  const [urlDraft, setUrlDraft] = useState('');
  const [previewError, setPreviewError] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const commitUrl = () => {
    const v = urlDraft.trim();
    if (!v) return;
    onChange(v);
    setUrlDraft('');
    setPreviewError(false);
  };

  const handleFile = (file: File | null | undefined) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Invalid file', 'Pick an image (PNG, JPG, WebP).');
      return;
    }
    if (file.size > maxBytes) {
      toast.error(
        'Image too large',
        `Max ${Math.round(maxBytes / 1024)} KB. Compress / resize first.`,
      );
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      onChange(String(reader.result ?? ''));
      setPreviewError(false);
      toast.success('Image attached', file.name);
    };
    reader.onerror = () => toast.error('Read failed', 'Could not read the image.');
    reader.readAsDataURL(file);
  };

  const clear = () => {
    onChange('');
    setPreviewError(false);
  };

  const inputCls =
    'h-10 min-w-0 flex-1 bg-transparent text-sm text-content-primary outline-none placeholder:text-content-muted';

  const isDataUrl = value.startsWith('data:');
  const sizeStyle = { width: previewSize, height: previewSize };

  return (
    <div className="flex flex-col gap-2">
      {label && (
        <span className="text-xs font-medium text-content-secondary">{label}</span>
      )}

      <div className="flex flex-wrap items-start gap-2">
        {/* Live preview tile — falls back to the broken-image glyph
            if the URL doesn't load. */}
        <div
          className="flex shrink-0 items-center justify-center overflow-hidden rounded-md border border-border bg-surface-card-hover"
          style={sizeStyle}
        >
          {value && !previewError ? (
            <img
              src={value}
              alt="preview"
              className="h-full w-full object-cover"
              onError={() => setPreviewError(true)}
            />
          ) : (
            <ImageOff className="h-5 w-5 text-content-muted" />
          )}
        </div>

        {/* Controls column — URL row on top, action row underneath. */}
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          {/* URL row */}
          <div className="flex min-w-0 items-center gap-2 rounded-md border border-border bg-surface-card px-2">
            <LinkIcon className="h-3.5 w-3.5 shrink-0 text-content-muted" />
            <input
              type="url"
              value={urlDraft}
              onChange={(e) => setUrlDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  commitUrl();
                }
              }}
              placeholder="Paste image URL and press Enter"
              maxLength={2048}
              disabled={disabled}
              className={inputCls}
            />
            <button
              type="button"
              onClick={commitUrl}
              disabled={disabled || !urlDraft.trim()}
              className="rounded-md bg-primary px-2 py-1 text-xs font-semibold text-primary-foreground hover:bg-primary-hover disabled:opacity-40"
            >
              Set
            </button>
          </div>

          {/* Action row */}
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={disabled}
              className="inline-flex h-8 items-center gap-1 rounded-md border border-border px-3 text-xs text-content-secondary hover:bg-surface-card-hover disabled:opacity-40"
            >
              <Upload className="h-3.5 w-3.5" /> Upload
            </button>
            <button
              type="button"
              onClick={() => value && window.open(value, '_blank', 'noopener')}
              disabled={!value}
              className="inline-flex h-8 items-center gap-1 rounded-md border border-border px-3 text-xs text-content-secondary hover:bg-surface-card-hover disabled:opacity-40"
              title="Open preview in new tab"
            >
              <Eye className="h-3.5 w-3.5" /> Preview
            </button>
            {value && (
              <button
                type="button"
                onClick={clear}
                disabled={disabled}
                className="inline-flex h-8 items-center gap-1 rounded-md border border-border px-3 text-xs text-danger hover:bg-surface-card-hover"
              >
                <X className="h-3.5 w-3.5" /> Remove
              </button>
            )}
            <span className="text-[11px] text-content-muted">
              {isDataUrl ? 'Uploaded preview · stored locally' : value ? 'Hosted URL' : 'No image'}
            </span>
          </div>

          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/svg+xml"
            hidden
            onChange={(e) => {
              handleFile(e.target.files?.[0]);
              e.target.value = '';
            }}
          />
        </div>
      </div>

      {hint && <p className="text-[11px] text-content-muted">{hint}</p>}
    </div>
  );
}
